import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';
import { formatNumber } from '../../../../../shared/utils/numberFormatter';

const WalletBalanceDistributionChart = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [cdfData, setCdfData] = useState([]);
  const [walletStats, setWalletStats] = useState({ avgBalance: 0, medianBalance: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Fetch wallet balance distribution data
  const fetchWalletBalanceDistribution = useCallback(async () => {
    if (!teamId || parentLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const params = { 
        team_id: teamId
      };

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      const response = await axiosInstance.get('/overview/wallet-balance-distribution', { params });

      if (response.data?.success) {
        setCdfData(response.data.data || []);
        setWalletStats(response.data.stats || { avgBalance: 0, medianBalance: 0 });
      } else {
        console.warn('Wallet balance distribution response not successful:', response.data);
        setCdfData([]);
        setWalletStats({ avgBalance: 0, medianBalance: 0 });
      }
    } catch (error) {
      console.error('Error fetching wallet balance distribution:', error);
      setCdfData([]);
      setWalletStats({ avgBalance: 0, medianBalance: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [teamId, appliedFilters.contracts, parentLoading]);

  useEffect(() => {
    fetchWalletBalanceDistribution();
  }, [fetchWalletBalanceDistribution]);

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div 
          className="bg-white p-3 border shadow-lg rounded-lg"
          style={{ 
            borderColor: styles.borderColor,
            fontFamily: 'Raleway, sans-serif'
          }}
        >
          <p className="text-sm font-medium mb-1" style={{ color: styles.textPrimary }}>
            Balance: {formatNumber(data.balance)}
          </p>
          <p className="text-xs" style={{ color: styles.textSecondary }}>
            Cumulative: <span className="font-medium">{data.cumulativePercent.toFixed(2)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading || parentLoading) {
    return (
      <div 
        className="p-2 md:p-3"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading wallet balance distribution...
          </div>
        </div>
      </div>
    );
  }

  if (!cdfData || cdfData.length === 0) {
    return (
      <div 
        className="p-2 md:p-3"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="mb-2">
          <h3 
            className="text-sm font-medium"
            style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
          >
            Wallet Balance Distribution
          </h3>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No wallet balance data available
          </div>
        </div>
      </div>
    );
  }

  // Format balance for X-axis
  const formatBalance = (value) => {
    return formatNumber(value);
  };

  // Format percentage for Y-axis
  const formatPercent = (value) => {
    return `${value.toFixed(0)}%`;
  };

  return (
    <div 
      className="p-2 md:p-3 flex flex-col"
      style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
    >
      <div className="mb-2">
        <h3 
          className="text-sm font-medium"
          style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
        >
          Wallet Balance Distribution
        </h3>
      </div>

      <div style={{ height: '250px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={cdfData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} horizontal={true} />
            <XAxis 
              dataKey="balance"
              tick={{ 
                fill: styles.textPrimary, 
                fontSize: 11,
                fontFamily: 'Raleway, sans-serif'
              }}
              axisLine={{ stroke: styles.borderColor }}
              tickFormatter={formatBalance}
            />
            <YAxis 
              tick={{ 
                fill: styles.textPrimary, 
                fontSize: 11,
                fontFamily: 'Raleway, sans-serif'
              }}
              axisLine={{ stroke: styles.borderColor }}
              tickFormatter={formatPercent}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="cumulativePercent"
              stroke={styles.accentColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={true}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Average and Median Wallet Balance */}
      {walletStats.avgBalance !== undefined && walletStats.medianBalance !== undefined && (
        <div className="flex justify-around items-center mt-1 pt-1" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
          <div className="text-center">
            <div className="text-xs mb-1" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Average Balance
            </div>
            <div className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              {formatNumber(walletStats.avgBalance)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs mb-1" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Median Balance
            </div>
            <div className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              {formatNumber(walletStats.medianBalance)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletBalanceDistributionChart;


